import type { FieldDefinitionType } from '#components/dynamicForm/types/field.type';
import { type FilterDefinition } from '@carrefour-gcs/bff-sdk';
import { DEFAULT_CARREFOUR_REFERENTIAL_INTERVAL, type CarrefourReferentialIntervalType } from '@carrefour-gcs/shared';
import { defineStore } from 'pinia';
import { useFiltersStore } from './filters.store';
import { resolveInitialFieldValue } from './utils/filterMapping/filterMapping.utils';
import { getFieldDefinitionValueOptions, sanitizeValueAgainstOptions } from './utils/formFields.utils';

export const useFormFieldsStore = defineStore('form-fields', {
  state: () => ({
    // Holds the structural configuration definitions from the API
    definitions: [] as FilterDefinition[],
    // Acts as our single source of truth for applied values
    currentFieldsValue: {} as Record<string, Record<string, unknown>>,
    loading: false,
    error: null as string | null
  }),

  getters: {
    fields(state): FieldDefinitionType[] {
      const filtersStore = useFiltersStore();

      // Cast safely for dynamic reading
      const rootFilters = filtersStore.filters as Record<string, unknown>;
      const legacySelectedFilters = (filtersStore.filters?.selectedFilters ?? {}) as Record<string, unknown>;

      return state.definitions.map((def) => {
        const resolvedValue = sanitizeValueAgainstOptions(
          def,
          resolveInitialFieldValue(def, state.currentFieldsValue, rootFilters, legacySelectedFilters)
        );

        return {
          id: def.id,
          element: def.type,
          fieldLabel: `filters.field.name.${def.name}`,
          fieldName: def.name,
          isPaginated: def.isPaginated,
          isSearchMandatory: def.isSearchMandatory,
          isSearchable: def.isSearchable,
          validationRules: def.filterDefinitionRules,
          options: getFieldDefinitionValueOptions(def),
          nestedFields: [],
          initialValue: resolvedValue ?? null,
          dependency: def.dependency,
          dependencyValue: def.dependencyValue,
          icon: def.icon,
          order: def.displayOrder,
          group: def.group
        };
      });
    }
  },
  actions: {
    async setDefinitions({
      response,
      isSuccess,
      isFetching,
      isError,
      error
    }: {
      response: FilterDefinition[];
      isSuccess: boolean;
      isFetching: boolean;
      isError: boolean;
      error: unknown | null;
    }) {
      this.loading = isFetching;
      this.error = isError ? 'Failed to load form' : null;

      if (isError) {
        console.error('Failed fetching filter definitions:', error);
      }

      if (isSuccess && response) {
        // Store the raw definitions configuration directly
        this.definitions = response;

        // Initialize currentFieldsValue with resolved initial values
        this.initializeFieldValues();
      }
    },

    /**
     * Initializes currentFieldsValue with resolved field values from definitions and filters store
     */
    initializeFieldValues() {
      const filtersStore = useFiltersStore();
      const rootFilters = filtersStore.filters as Record<string, unknown>;
      const legacySelectedFilters = (filtersStore.filters?.selectedFilters ?? {}) as Record<string, unknown>;

      const initialValues: Record<string, Record<string, unknown>> = {};

      this.definitions.forEach((def) => {
        const resolvedValue = sanitizeValueAgainstOptions(
          def,
          resolveInitialFieldValue(def, this.currentFieldsValue, rootFilters, legacySelectedFilters)
        );

        if (resolvedValue !== null && resolvedValue !== undefined) {
          const groupName = def.group?.name ?? '';
          if (!initialValues[groupName]) {
            initialValues[groupName] = {};
          }
          initialValues[groupName][def.name] = resolvedValue;
        }
      });

      this.currentFieldsValue = initialValues;
    },

    /**
     * Hydrates the cache with the last submitted values for a specific filter group.
     */
    setLastSubmitted({
      groupName,
      data,
      referentialIntervalKey = DEFAULT_CARREFOUR_REFERENTIAL_INTERVAL
    }: {
      groupName: string;
      data: Record<string, unknown>;
      referentialIntervalKey?: keyof CarrefourReferentialIntervalType;
    }) {
      this.currentFieldsValue[groupName] = data;

      // Cross-communicate directly with global filters store
      // We send to filters store a flattened object
      const filtersStore = useFiltersStore();

      filtersStore.setDynamicFilters(
        Object.values(this.currentFieldsValue).reduce((acc, val) => ({ ...acc, ...val }), {}),
        referentialIntervalKey
      );
    }
  }
});
